import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, AlertTriangle, Check, ChevronRight } from 'lucide-react';
import { ExtractedTradeFields } from '../../types/confirmation';
import { GroupingsConfig } from '../../types/groupings';
import { clientService as apiClientService, TradingEntity } from '../../services/api/clientService';
import { bankService, Bank } from '../../services/api/bankService';
import { groupingsService } from '../../services/api/groupingsService';
import { apiClient } from '../../services/api';
import { parseAmountInput } from '../../utils/amountFormatter';
import { humanizeFieldName } from '../../utils/fieldNames';
import './SubmitAsTradeModal.css';

interface SubmitAsTradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  emailId: string;
  extractedTrades: ExtractedTradeFields[];
  emailTradeIndex?: number;  // KAN-191: index within the email's llmExtractedData.Trades[]
  onTradeCreated: (tradeNumber: string, matched: boolean) => void;
}

interface SubmitFormData {
  ourPartyId: string;
  ourParty: string;
  ourPartyTaxId: string;
  counterpartyId: string;
  counterpartyName: string;
  counterpartyTaxId: string;
  spotPrice: string;
  forwardPoints: string;
  hedgeObject: string;
  grouping1: string;
  grouping2: string;
  grouping3: string;
  notes: string;
}

const initialFormData: SubmitFormData = {
  ourPartyId: '',
  ourParty: '',
  ourPartyTaxId: '',
  counterpartyId: '',
  counterpartyName: '',
  counterpartyTaxId: '',
  spotPrice: '',
  forwardPoints: '',
  hedgeObject: '',
  grouping1: '',
  grouping2: '',
  grouping3: '',
  notes: '',
};

const SubmitAsTradeModal: React.FC<SubmitAsTradeModalProps> = ({
  isOpen,
  onClose,
  clientId,
  emailId,
  extractedTrades,
  emailTradeIndex,
  onTradeCreated,
}) => {
  const { t } = useTranslation();

  // Trade selection for multi-trade emails
  const [selectedTradeIndex, setSelectedTradeIndex] = useState<number | null>(
    extractedTrades.length === 1 ? 0 : null
  );

  // Reference data
  const [entities, setEntities] = useState<TradingEntity[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);
  const [loadingRefData, setLoadingRefData] = useState(true);

  // Form state
  const [formData, setFormData] = useState<SubmitFormData>(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [blockedFields, setBlockedFields] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const selectedTrade = selectedTradeIndex !== null ? extractedTrades[selectedTradeIndex] : null;
  const isForward = selectedTrade?.ProductType?.toLowerCase() === 'forward';

  // Load reference data on mount
  useEffect(() => {
    if (!isOpen) return;
    loadReferenceData();
  }, [isOpen, clientId]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedTradeIndex(extractedTrades.length === 1 ? 0 : null);
      setFormData(initialFormData);
      setSubmitting(false);
      setSubmitError(null);
      setBlockedFields([]);
      setValidationErrors({});
    }
  }, [isOpen]);

  // Auto-resolve counterparty when trade selected + banks loaded
  useEffect(() => {
    if (!selectedTrade || banks.length === 0) return;
    resolveCounterparty(selectedTrade.CounterpartyName || '');
  }, [selectedTradeIndex, banks]);

  const loadReferenceData = async () => {
    setLoadingRefData(true);
    try {
      const [entitiesResult, banksResult, grpConfig] = await Promise.all([
        apiClientService.getTradingEntities(clientId),
        bankService.listBanks(),
        groupingsService.getGroupings(clientId),
      ]);
      if (entitiesResult.success && entitiesResult.data) {
        setEntities(entitiesResult.data);
        // Auto-select if only one entity
        if (entitiesResult.data.length === 1) {
          const e = entitiesResult.data[0];
          setFormData(prev => ({
            ...prev,
            ourPartyId: e.id,
            ourParty: e.name,
            ourPartyTaxId: e.taxId || '',
          }));
        }
      }
      if (banksResult.success && banksResult.data) {
        setBanks(banksResult.data.filter(b => b.isCounterparty !== false && b.status === 'active'));
      }
      if (grpConfig) {
        setGroupingsConfig(grpConfig);
      }
    } catch (err) {
      console.error('Failed to load reference data:', err);
    } finally {
      setLoadingRefData(false);
    }
  };

  const resolveCounterparty = (extractedName: string) => {
    if (!extractedName) return;
    const lower = extractedName.toLowerCase();

    // Exact match first
    let match = banks.find(b => b.name.toLowerCase() === lower);

    // Substring/contains match
    if (!match) {
      match = banks.find(b =>
        b.name.toLowerCase().includes(lower) || lower.includes(b.name.toLowerCase())
      );
    }

    if (match) {
      setFormData(prev => ({
        ...prev,
        counterpartyId: match!.id || '',
        counterpartyName: match!.name,
        counterpartyTaxId: match!.taxId || '',
      }));
    }
  };

  // Filter grouping values by selected entity (mirrors TradeBooking.tsx logic)
  const getFilteredGroupingValues = (groupingNumber: 1 | 2 | 3) => {
    if (!groupingsConfig || !formData.ourPartyId) return [];
    const entityMapping = groupingsConfig.entityMappings.find(m => m.entityId === formData.ourPartyId);
    if (!entityMapping) return [];

    if (groupingNumber === 1) {
      return groupingsConfig.grouping1Values.filter(v => v.isActive && entityMapping.grouping1ValueIds.includes(v.id));
    }

    if (groupingNumber === 2) {
      if (groupingsConfig.grouping2Mode === 'subgrouping') {
        const selectedG1 = groupingsConfig.grouping1Values.find(v => v.name === formData.grouping1);
        const allowedIds = selectedG1 ? entityMapping.g1ToG2Mappings?.[selectedG1.id] || [] : [];
        return groupingsConfig.grouping2Values.filter(v => v.isActive && allowedIds.includes(v.id));
      }
      return groupingsConfig.grouping2Values.filter(v => v.isActive && entityMapping.grouping2ValueIds.includes(v.id));
    }

    if (groupingNumber === 3) {
      if (groupingsConfig.grouping3Mode === 'subgrouping') {
        const selectedG2 = groupingsConfig.grouping2Values.find(v => v.name === formData.grouping2);
        const allowedIds = selectedG2 ? entityMapping.g2ToG3Mappings?.[selectedG2.id] || [] : [];
        return groupingsConfig.grouping3Values.filter(v => v.isActive && allowedIds.includes(v.id));
      }
      return groupingsConfig.grouping3Values.filter(v => v.isActive && entityMapping.grouping3ValueIds.includes(v.id));
    }

    return [];
  };

  const handleEntityChange = (entityId: string) => {
    const entity = entities.find(e => e.id === entityId);
    if (entity) {
      setFormData(prev => ({
        ...prev,
        ourPartyId: entity.id,
        ourParty: entity.name,
        ourPartyTaxId: entity.taxId || '',
        // Reset groupings when entity changes (values are entity-specific)
        grouping1: '',
        grouping2: '',
        grouping3: '',
      }));
    }
    setValidationErrors(prev => { const next = { ...prev }; delete next.ourPartyId; return next; });
  };

  const handleCounterpartyChange = (bankId: string) => {
    const bank = banks.find(b => b.id === bankId);
    if (bank) {
      setFormData(prev => ({
        ...prev,
        counterpartyId: bank.id || '',
        counterpartyName: bank.name,
        counterpartyTaxId: bank.taxId || '',
      }));
    }
    setValidationErrors(prev => { const next = { ...prev }; delete next.counterpartyId; return next; });
  };

  const handleFieldChange = (field: keyof SubmitFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setValidationErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
  };

  const handleGrouping1Change = (value: string) => {
    setFormData(prev => ({
      ...prev,
      grouping1: value,
      // Reset downstream groupings in subgrouping mode
      ...(groupingsConfig?.grouping2Mode === 'subgrouping' ? { grouping2: '', grouping3: '' } : {}),
      ...(groupingsConfig?.grouping3Mode === 'subgrouping' && groupingsConfig?.grouping2Mode !== 'subgrouping' ? { grouping3: '' } : {}),
    }));
  };

  const handleGrouping2Change = (value: string) => {
    setFormData(prev => ({
      ...prev,
      grouping2: value,
      ...(groupingsConfig?.grouping3Mode === 'subgrouping' ? { grouping3: '' } : {}),
    }));
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.ourPartyId) {
      errors.ourPartyId = t('confirmations.exceptions.fieldRequired');
    }
    if (!formData.counterpartyId) {
      errors.counterpartyId = t('confirmations.exceptions.fieldRequired');
    }
    if (!selectedTrade) {
      errors.trade = t('confirmations.exceptions.fieldRequired');
      return false;
    }

    // Check required fields from extraction
    if (!selectedTrade.Direction) errors.direction = t('confirmations.exceptions.fieldRequired');
    if (!selectedTrade.Currency1) errors.currency1 = t('confirmations.exceptions.fieldRequired');
    if (!selectedTrade.Currency2) errors.currency2 = t('confirmations.exceptions.fieldRequired');
    if (!selectedTrade.QuantityCurrency1) errors.amount = t('confirmations.exceptions.fieldRequired');
    if (!selectedTrade.Price) errors.price = t('confirmations.exceptions.fieldRequired');
    if (!selectedTrade.TradeDate) errors.tradeDate = t('confirmations.exceptions.fieldRequired');

    // Forward-specific required
    if (isForward) {
      if (!selectedTrade.MaturityDate) errors.maturityDate = t('confirmations.exceptions.fieldRequired');
      if (!selectedTrade.SettlementType) errors.settlementType = t('confirmations.exceptions.fieldRequired');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (skipBlockCheck = false) => {
    if (!validate() || !selectedTrade) return;

    setSubmitting(true);
    setSubmitError(null);
    if (!skipBlockCheck) setBlockedFields([]);

    try {
      // Build trade data in PascalCase — directly from extraction (already correct format)
      const tradeData: Record<string, any> = {
        // From extraction
        BankTradeNumber: selectedTrade.BankTradeNumber || '',
        Direction: selectedTrade.Direction,
        ProductType: selectedTrade.ProductType || 'Spot',
        Currency1: selectedTrade.Currency1,
        Currency2: selectedTrade.Currency2,
        QuantityCurrency1: selectedTrade.QuantityCurrency1 || 0,
        QuantityCurrency2: selectedTrade.QuantityCurrency2 || 0,
        Price: selectedTrade.Price || 0,
        TradeDate: selectedTrade.TradeDate || '',
        ValueDate: selectedTrade.ValueDate || '',
        MaturityDate: selectedTrade.MaturityDate || '',
        PaymentDate: selectedTrade.PaymentDate || '',
        Currency1PaymentDate: selectedTrade.Currency1PaymentDate || '',
        Currency2PaymentDate: selectedTrade.Currency2PaymentDate || '',
        FixingReference: selectedTrade.FixingReference || '',
        FixingOffset: selectedTrade.FixingOffset || '',
        SettlementType: selectedTrade.SettlementType || '',
        SettlementCurrency: selectedTrade.SettlementCurrency || '',
        IsAverageRateForward: selectedTrade.IsAverageRateForward || false,
        AverageRateFixingSchedule: selectedTrade.AverageRateFixingDates
          ? selectedTrade.AverageRateFixingDates.map(d => ({ Date: d }))
          : [],

        // From form inputs
        OurParty: formData.ourParty,
        OurPartyId: formData.ourPartyId,
        OurPartyTaxId: formData.ourPartyTaxId,
        CounterpartyName: formData.counterpartyName,
        CounterpartyId: formData.counterpartyId,
        CounterpartyTaxId: formData.counterpartyTaxId,
        Notes: formData.notes,
        Grouping1: formData.grouping1,
        Grouping2: formData.grouping2,
        Grouping3: formData.grouping3,
        HedgeObject: formData.hedgeObject,
      };

      // Add spot price / forward points (Forward only)
      if (isForward && formData.spotPrice) {
        tradeData.SpotPrice = parseAmountInput(formData.spotPrice);
      }
      if (isForward) {
        tradeData.ForwardPoints = parseAmountInput(formData.forwardPoints) ?? 0;
      }

      // Call existing book_trade endpoint with sourceEmailId
      // KAN-191: Pass BankTradeNumber as the primary identifier (unambiguous),
      // with emailTradeIndex as fallback for legacy compatibility
      const btn = selectedTrade.BankTradeNumber || '';
      let url = `/api/v1/clients/${clientId}/trades/book?workflowState=booked&sourceEmailId=${emailId}&sourceEmailTradeIndex=${emailTradeIndex ?? selectedTradeIndex}&sourceBankTradeNumber=${encodeURIComponent(btn)}`;
      // KAN-199: Skip pre-flight block check when user explicitly chooses "Create at Booked"
      if (skipBlockCheck) {
        url += '&skipTransitionBlockCheck=true';
      }
      const response = await apiClient.post<{
        trade_id: string;
        trade_number: string;
        workflow_state: string;
        match_result?: {
          matched: boolean;
          matchId?: string;
          status?: string;
          error?: string;
        };
      }>(url, tradeData);

      const tradeNumber = response.data.trade_number;
      const matched = response.data.match_result?.matched ?? false;

      onTradeCreated(tradeNumber, matched);
      onClose();
    } catch (err: any) {
      // KAN-199: Detect structured transition block error (409 with blocked: true)
      if (err?.detail?.blocked && err?.statusCode === 409) {
        setBlockedFields(err.detail.missingFields || []);
        setSubmitError(err.detail.message || err.message);
      } else {
        setBlockedFields([]);
        const message = err?.detail?.message || err?.message || t('confirmations.exceptions.submitTradeError');
        setSubmitError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Helper: format amount for display
  const fmtAmt = (val: number | string | undefined, ccy?: string) => {
    if (val == null || val === '') return '-';
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return String(val);
    const formatted = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
    return ccy ? `${ccy} ${formatted}` : formatted;
  };

  if (!isOpen) return null;

  // ── Multi-trade selection step ──
  if (selectedTradeIndex === null) {
    return (
      <div className="sat-modal-overlay" onClick={onClose}>
        <div className="sat-modal sat-modal-narrow" onClick={e => e.stopPropagation()}>
          <div className="sat-modal-header">
            <h2>{t('confirmations.exceptions.submitAsTradeTitle')}</h2>
            <button className="sat-close-btn" onClick={onClose}><X size={20} /></button>
          </div>
          <div className="sat-modal-body">
            <p className="sat-select-prompt">
              {t('confirmations.exceptions.selectTradeToEnter', { count: extractedTrades.length })}
            </p>
            <div className="sat-trade-selection-list">
              {extractedTrades.map((trade, idx) => (
                <button
                  key={idx}
                  className="sat-trade-selection-item"
                  onClick={() => setSelectedTradeIndex(idx)}
                >
                  <div className="sat-trade-sel-main">
                    <span className="sat-trade-sel-index">#{idx + 1}</span>
                    {trade.Direction && (
                      <span className={`sat-trade-sel-direction ${trade.Direction.toLowerCase()}`}>
                        {trade.Direction}
                      </span>
                    )}
                    <span className="sat-trade-sel-amount">
                      {fmtAmt(trade.QuantityCurrency1, trade.Currency1)}
                    </span>
                    {trade.Currency2 && <span className="sat-trade-sel-ccy2">/ {trade.Currency2}</span>}
                    {trade.ProductType && <span className="sat-trade-sel-type">{trade.ProductType}</span>}
                  </div>
                  <div className="sat-trade-sel-details">
                    {trade.CounterpartyName && <span>{trade.CounterpartyName}</span>}
                    {trade.TradeDate && <span>{trade.TradeDate}</span>}
                  </div>
                  <ChevronRight size={16} className="sat-trade-sel-arrow" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Main form ──
  return (
    <div className="sat-modal-overlay" onClick={onClose}>
      <div className="sat-modal" onClick={e => e.stopPropagation()}>
        <div className="sat-modal-header">
          <h2>{t('confirmations.exceptions.submitAsTradeTitle')}</h2>
          <button className="sat-close-btn" onClick={onClose} disabled={submitting}><X size={20} /></button>
        </div>

        {loadingRefData ? (
          <div className="sat-loading">
            <Loader2 size={32} className="spinner-icon" />
          </div>
        ) : (
          <div className="sat-modal-body">
            {/* Pre-filled summary section */}
            <div className="sat-section">
              <div className="sat-section-label">{t('confirmations.exceptions.preFilledSummary')}</div>
              <div className="sat-prefilled-grid">
                {selectedTrade.CounterpartyName && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.counterparty')}</span>
                    <span className="sat-pf-value">{selectedTrade.CounterpartyName}</span>
                  </div>
                )}
                {selectedTrade.Direction && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.direction')}</span>
                    <span className={`sat-pf-value direction-${selectedTrade.Direction.toLowerCase()}`}>
                      {selectedTrade.Direction}
                    </span>
                  </div>
                )}
                {selectedTrade.ProductType && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.productType')}</span>
                    <span className="sat-pf-value">{selectedTrade.ProductType}</span>
                  </div>
                )}
                {selectedTrade.Currency1 && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.currency1')}/{t('grid.columns.currency2')}</span>
                    <span className="sat-pf-value">{selectedTrade.Currency1}{selectedTrade.Currency2 ? `/${selectedTrade.Currency2}` : ''}</span>
                  </div>
                )}
                {selectedTrade.QuantityCurrency1 != null && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.amount')}</span>
                    <span className="sat-pf-value">{fmtAmt(selectedTrade.QuantityCurrency1, selectedTrade.Currency1)}</span>
                  </div>
                )}
                {selectedTrade.Price != null && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.price')}</span>
                    <span className="sat-pf-value">{selectedTrade.Price}</span>
                  </div>
                )}
                {selectedTrade.QuantityCurrency2 != null && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.contraAmount')}</span>
                    <span className="sat-pf-value">{fmtAmt(selectedTrade.QuantityCurrency2, selectedTrade.Currency2)}</span>
                  </div>
                )}
                {selectedTrade.TradeDate && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.tradeDate')}</span>
                    <span className="sat-pf-value">{selectedTrade.TradeDate}</span>
                  </div>
                )}
                {selectedTrade.ValueDate && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.valueDate')}</span>
                    <span className="sat-pf-value">{selectedTrade.ValueDate}</span>
                  </div>
                )}
                {selectedTrade.MaturityDate && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.maturityDate')}</span>
                    <span className="sat-pf-value">{selectedTrade.MaturityDate}</span>
                  </div>
                )}
                {selectedTrade.SettlementType && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.settlementType')}</span>
                    <span className="sat-pf-value">{selectedTrade.SettlementType}</span>
                  </div>
                )}
                {selectedTrade.SettlementCurrency && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.settlementCurrency')}</span>
                    <span className="sat-pf-value">{selectedTrade.SettlementCurrency}</span>
                  </div>
                )}
                {selectedTrade.FixingReference && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.fixingReference')}</span>
                    <span className="sat-pf-value">{selectedTrade.FixingReference}</span>
                  </div>
                )}
                {selectedTrade.FixingOffset && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.fixingOffset')}</span>
                    <span className="sat-pf-value">{selectedTrade.FixingOffset}</span>
                  </div>
                )}
                {selectedTrade.BankTradeNumber && (
                  <div className="sat-pf-item">
                    <span className="sat-pf-label">{t('grid.columns.bankTradeNumber')}</span>
                    <span className="sat-pf-value">{selectedTrade.BankTradeNumber}</span>
                  </div>
                )}
              </div>
              <p className="sat-prefilled-note">{t('confirmations.exceptions.preFilledNote')}</p>
            </div>

            {/* User input section */}
            <div className="sat-section">
              <div className="sat-section-label">{t('confirmations.exceptions.completeFollowing')}</div>
              <div className="sat-form-grid">
                {/* OurParty (required) */}
                <div className={`sat-field ${validationErrors.ourPartyId ? 'has-error' : ''}`}>
                  <label>{t('tradeFields.ourParty')} *</label>
                  <select
                    value={formData.ourPartyId}
                    onChange={e => handleEntityChange(e.target.value)}
                    disabled={submitting}
                  >
                    <option value="">{t('common.select')}</option>
                    {entities.map(e => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                  {validationErrors.ourPartyId && <span className="sat-field-error">{validationErrors.ourPartyId}</span>}
                </div>

                {/* CounterpartyName (required, pre-resolved) */}
                <div className={`sat-field ${validationErrors.counterpartyId ? 'has-error' : ''}`}>
                  <label>{t('grid.columns.counterparty')} *</label>
                  <select
                    value={formData.counterpartyId}
                    onChange={e => handleCounterpartyChange(e.target.value)}
                    disabled={submitting}
                  >
                    <option value="">{t('common.select')}</option>
                    {banks.map(b => (
                      <option key={b.id} value={b.id!}>{b.name}</option>
                    ))}
                  </select>
                  {validationErrors.counterpartyId && <span className="sat-field-error">{validationErrors.counterpartyId}</span>}
                </div>

                {/* SpotPrice (Forward only) */}
                {isForward && (
                  <div className="sat-field">
                    <label>{t('tradeBooking.tradeDetails.spotPrice')}</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.spotPrice}
                      onChange={e => handleFieldChange('spotPrice', e.target.value)}
                      disabled={submitting}
                      placeholder="0.00"
                    />
                  </div>
                )}

                {/* ForwardPoints (Forward only) */}
                {isForward && (
                  <div className="sat-field">
                    <label>{t('tradeBooking.tradeDetails.forwardPoints')}</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.forwardPoints}
                      onChange={e => handleFieldChange('forwardPoints', e.target.value)}
                      disabled={submitting}
                      placeholder="0.00"
                    />
                  </div>
                )}

                {/* HedgeObject */}
                <div className="sat-field">
                  <label>{t('tradeBooking.administrative.hedgeObject')}</label>
                  <input
                    type="text"
                    value={formData.hedgeObject}
                    onChange={e => handleFieldChange('hedgeObject', e.target.value)}
                    disabled={submitting}
                  />
                </div>

                {/* Grouping1 (always shown if config exists) */}
                {groupingsConfig && (
                  <div className="sat-field">
                    <label>{groupingsConfig.grouping1Label || t('tradeBooking.classification.grouping1')}</label>
                    <select
                      value={formData.grouping1}
                      onChange={e => handleGrouping1Change(e.target.value)}
                      disabled={submitting || !formData.ourPartyId}
                    >
                      <option value="">{t('tradeBooking.classification.select')}</option>
                      {getFilteredGroupingValues(1).map(v => (
                        <option key={v.id} value={v.name}>{v.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Grouping2 (only if label configured) */}
                {groupingsConfig && groupingsConfig.grouping2Label && (
                  <div className="sat-field">
                    <label>{groupingsConfig.grouping2Label}</label>
                    <select
                      value={formData.grouping2}
                      onChange={e => handleGrouping2Change(e.target.value)}
                      disabled={submitting || !formData.ourPartyId}
                    >
                      <option value="">{t('tradeBooking.classification.select')}</option>
                      {getFilteredGroupingValues(2).map(v => (
                        <option key={v.id} value={v.name}>{v.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Grouping3 (only if label configured) */}
                {groupingsConfig && groupingsConfig.grouping3Label && (
                  <div className="sat-field">
                    <label>{groupingsConfig.grouping3Label}</label>
                    <select
                      value={formData.grouping3}
                      onChange={e => handleFieldChange('grouping3', e.target.value)}
                      disabled={submitting || !formData.ourPartyId || (groupingsConfig.grouping3Mode === 'subgrouping' && !formData.grouping2)}
                    >
                      <option value="">{t('tradeBooking.classification.select')}</option>
                      {getFilteredGroupingValues(3).map(v => (
                        <option key={v.id} value={v.name}>{v.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Notes (full width) */}
                <div className="sat-field sat-field-full">
                  <label>{t('tradeBooking.administrative.notes')}</label>
                  <textarea
                    value={formData.notes}
                    onChange={e => handleFieldChange('notes', e.target.value)}
                    disabled={submitting}
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* KAN-199: Transition block warning */}
            {blockedFields.length > 0 && submitError && (
              <div className="sat-error-banner sat-block-warning">
                <AlertTriangle size={16} />
                <div>
                  <strong>{t('trades.transitionBlock.preflightError', 'The following fields must be completed before this trade can advance:')}</strong>
                  <ul className="sat-blocked-fields-list">
                    {blockedFields.map(f => (
                      <li key={f}>{t(`tradeFields.${f}`, humanizeFieldName(f))}</li>
                    ))}
                  </ul>
                  <span className="sat-block-hint">
                    {t('trades.transitionBlock.preflightHint', 'Fill in the fields above, or click "Create at Booked" to create the trade without auto-advancing.')}
                  </span>
                </div>
              </div>
            )}

            {/* Error banner (non-block errors) */}
            {submitError && blockedFields.length === 0 && (
              <div className="sat-error-banner">
                <AlertTriangle size={16} />
                <span>{submitError}</span>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        {!loadingRefData && (
          <div className="sat-modal-footer">
            <button
              className="sat-cancel-btn"
              onClick={onClose}
              disabled={submitting}
            >
              {t('common.cancel')}
            </button>
            {/* KAN-199: "Create at Booked" escape hatch when block is active */}
            {blockedFields.length > 0 && (
              <button
                className="sat-cancel-btn"
                onClick={() => handleSubmit(true)}
                disabled={submitting}
              >
                {t('trades.transitionBlock.createAtBooked', 'Create at Booked')}
              </button>
            )}
            <button
              className="sat-submit-btn"
              onClick={() => handleSubmit(false)}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 size={16} className="spinner-icon" />
              ) : (
                <Check size={16} />
              )}
              <span>{t('confirmations.exceptions.submitTradeButton')}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmitAsTradeModal;
